import { inferUploadMime, isHeicMime } from "./upload-mime";
import { MAX_IMAGE_INPUT_BYTES, TARGET_IMAGE_BYTES, uploadImageDimensions } from "./image-upload-size";

/** Normalize photos locally before any upload. Videos pass through unchanged. */
export async function prepareMediaUploadFile(file: File): Promise<File> {
  const mime = inferUploadMime(file.name, file.type);
  if (!mime.startsWith("image/")) return mime && mime !== file.type
    ? new File([file], file.name, { type: mime, lastModified: file.lastModified }) : file;
  if (file.size > MAX_IMAGE_INPUT_BYTES) throw new Error("This photo exceeds 100MB. Export a smaller photo first so your phone can process it safely.");
  let image: Awaited<ReturnType<typeof decodeImage>>;
  try { image = await decodeImage(file); }
  catch {
    if (!isHeicMime(mime)) {
      if (["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"].includes(mime) && file.size <= 20 * 1024 * 1024) return new File([file], file.name, { type: mime });
      throw new Error("This image cannot be opened by your browser. Export as JPEG or use a supported photo below 20MB for server conversion.");
    }
    try {
      const { heicTo } = await import("heic-to/csp");
      const blob = await heicTo({ blob: file, type: "image/jpeg", quality: 0.9 });
      image = await decodeImage(blob);
    } catch {
      if (file.size <= 20 * 1024 * 1024) return new File([file], file.name, { type: mime });
      throw new Error("This iPhone photo could not be converted locally and exceeds the 20MB server limit. Export it as JPEG and try again.");
    }
  }
  try {
    const size = uploadImageDimensions(image.width, image.height);
    const canvas = document.createElement("canvas");
    canvas.width = size.width; canvas.height = size.height;
    try {
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Image processing is unavailable in this browser");
      ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, size.width, size.height);
      ctx.drawImage(image.source, 0, 0, size.width, size.height);
      for (const quality of [0.88, 0.72, 0.55]) {
        const result = await new Promise<Blob>((resolve, reject) => canvas.toBlob(value => value ? resolve(value) : reject(new Error("Photo compression failed")), "image/jpeg", quality));
        if (result.size <= TARGET_IMAGE_BYTES) return new File([result], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg", lastModified: file.lastModified });
      }
      throw new Error("This photo could not be compressed enough. Please choose a smaller image.");
    } finally { canvas.width = 0; canvas.height = 0; }
  } finally { image.close(); }
}

async function decodeImage(blob: Blob): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap !== "undefined") {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
      return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() };
    } catch { /* Safari may decode this format only through an image element. */ }
  }
  const url = URL.createObjectURL(blob);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error("Image decoding failed")); image.src = url; });
    return { source: image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (error) { URL.revokeObjectURL(url); throw error; }
}
